import React, { useMemo } from 'react';
import { CheckPicker } from 'rsuite';
import { complaintTypeLabelFactory } from '@/constants';
import styled from 'styled-components';
import { useComplaintsStatus } from '../../utils/useComplaintsStatus';
import { COMPLAINTS_COL_IDS } from '../complaints-constants';

const StyledCheckPicker = styled(CheckPicker)`
  width: 124px;
`;

const complaintTypesPlaceholder = 'All Types';
const statusPlaceholder = 'All Statuses';
// const zonesPlaceholder = 'All Zones';
const BLANKS = '(Blanks)';

/**
 * Custom hook to build check picker options, given a list of possible values and
 * a factory to generate user-friendly labels. The resuling options are memoized.
 * @param {T[]} fieldValues Values to generate check picker options for
 * @param {Function} fieldLabelFactory A function from values in `fieldValues`
 * to string descriptions to be displayed to the user
 * @param {boolean} extraOptions.allowBlanks Whether '(Blanks)' should be an option if
 * there are values that result in a null-ish label returned from `fieldLabelFactory`
 * @returns {Array} Array of options for check picker
 */
function useColumnFilterOptions(
  fieldValues,
  fieldLabelFactory,
  extraOptions = {}
) {
  const { allowBlanks = true } = extraOptions;

  return useMemo(() => {
    const optionBuilder = (agGridSetFilterItem) => ({
      label: fieldLabelFactory(agGridSetFilterItem),
      value: agGridSetFilterItem,
    });
    return fieldValues
      ?.map(optionBuilder)
      .filter(({ label }) => label || allowBlanks)
      .map(({ label, ...option }) => ({
        label: label || BLANKS,
        ...option,
      }));
  }, [fieldValues, fieldLabelFactory, allowBlanks]);
}

export function FilterDropdowns({
  statusItems,
  complaintTypeItems,
  // zoneItems,
  onFilterSelectCB,
}) {
  const { complaintStatusLabelFactory } = useComplaintsStatus();

  const statusOptions = useColumnFilterOptions(
    statusItems,
    complaintStatusLabelFactory,
    {
      allowBlanks: false,
    }
  );
  const complaintTypeOptions = useColumnFilterOptions(
    complaintTypeItems,
    complaintTypeLabelFactory
  );

  // const zoneOptions = useMemo(() => zoneItems?.map(checkPickerOptionBuilder),
  //   [zoneItems]);

  return (
    <>
      <StyledCheckPicker
        data={statusOptions || []}
        placeholder={statusPlaceholder}
        searchable={false}
        onSelect={onFilterSelectCB(COMPLAINTS_COL_IDS.status)}
        onClean={() => {
          onFilterSelectCB(COMPLAINTS_COL_IDS.status)();
        }}
      />
      <StyledCheckPicker
        data-cy="complaints-complaints-type-dropdown"
        data={complaintTypeOptions || []}
        placeholder={complaintTypesPlaceholder}
        searchable={false}
        onSelect={onFilterSelectCB(COMPLAINTS_COL_IDS.complaintTypes)}
        onClean={() => {
          onFilterSelectCB(COMPLAINTS_COL_IDS.complaintTypes)();
        }}
      />
      {/* Hiding this dropdown for the time being until it is required again later */}
      {/* <StyledCheckPicker
        data={zoneOptions}
        placeholder={zonesPlaceholder}
        searchable={false}
        onSelect={onFilterSelectCB('zones')}
        onClean={() => { onFilterSelectCB('zones')(); }}
      /> */}
    </>
  );
}
